import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Clock } from 'lucide-react';
import { PressRelease } from '../../data/pressReleases';

interface PressReleaseListProps {
  releases: PressRelease[];
  title?: string;
  showAll?: boolean;
}

const PressReleaseList: React.FC<PressReleaseListProps> = ({ 
  releases, 
  title = "Latest Press Releases",
  showAll = false 
}) => {
  const { isDarkMode } = useTheme();
  
  const displayReleases = showAll ? releases : releases.slice(0, 3);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      } 
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className={`text-3xl font-bold mb-4 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          {title}
        </h2>
        <p className={`text-lg ${
          isDarkMode ? 'text-gray-300' : 'text-gray-600'
        }`}>
          Stay updated with our latest announcements and company news
        </p>
      </div>

      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {displayReleases.map((release) => (
          <motion.article
            key={release.id}
            variants={itemVariants}
            className={`group rounded-2xl border ${
              isDarkMode 
                ? 'bg-gray-800/70 border-gray-700/50 hover:bg-gray-800' 
                : 'bg-white border-gray-200 hover:bg-gray-50'
            } overflow-hidden transition-all duration-300 hover:shadow-lg`}
          >
            <div className="p-6">
              {/* Category and Featured Badge */}
              <div className="flex items-center justify-between mb-4">
                <span className={`text-sm px-3 py-1 rounded-full ${
                  isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800'
                }`}>
                  {release.category}
                </span>
                {release.featured && (
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'
                  }`}>
                    Featured
                  </span>
                )}
              </div>

              {/* Title */}
              <h3 className={`text-xl font-bold mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {release.title}
              </h3>

              {/* Excerpt */}
              <p className={`text-sm mb-4 line-clamp-3 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {release.excerpt}
              </p>

              {/* Meta Information */}
              <div className={`flex items-center gap-4 text-xs mb-4 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {release.date}
                </div>
                {release.readTime && (
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {release.readTime}
                  </div>
                )}
              </div>

              {/* Read More Link */}
              <Link
                to={`/press/releases/${release.slug}`}
                className={`inline-flex items-center text-sm font-medium group-hover:gap-2 transition-all ${
                  isDarkMode 
                    ? 'text-blue-400 hover:text-blue-300' 
                    : 'text-blue-600 hover:text-blue-700'
                }`}
              >
                Read Full Release
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.article>
        ))}
      </motion.div>

      {!showAll && releases.length > 3 && (
        <div className="text-center">
          <Link
            to="/press/archive"
            className={`inline-flex items-center px-6 py-3 rounded-xl border transition-all ${
              isDarkMode
                ? 'border-gray-600 text-gray-300 hover:bg-gray-800 hover:border-gray-500'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
            }`}
          >
            View All Press Releases
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default PressReleaseList;
